import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { Check, ChevronDown, Search, X } from "lucide-react";
import styles from "./SearchableSelect.module.scss";
// Keyboard + placement logic shared with Select (same popup anatomy).
import {
  LIST_MAX_HEIGHT,
  computePopupPlacement,
  getViewportHeight,
  initialActiveIndex,
  isOptionEnabled,
  nextActiveIndex,
  samePlacement,
  scrollOptionIntoView,
} from "../Select/listbox.helpers";

/* ────────────────────────────────────────────────────────────
 * SearchableSelect — single-choice dropdown with a built-in
 * search input. Mirrors the MultiSelect primitive's structure
 * (popup, search bar, option list, footer) so the two feel like
 * siblings in the design system.
 *
 *   <SearchableSelect
 *     label="Employee"
 *     placeholder="Select Employee"
 *     value={selectedId}
 *     onChange={setSelectedId}
 *     options={[
 *       { value: 12, label: "Numaan Kazi", sublabel: "Engineering" },
 *       …
 *     ]}
 *   />
 *
 * value is a single primitive (string | number | null), matching
 * the shape callers already use against native `<select>`.
 *
 * Keyboard (WAI-ARIA editable combobox): opening moves focus into
 * the search box; the list is driven from there through the input's
 * aria-activedescendant, so typing and arrowing never fight over
 * focus.
 *   ArrowDown / ArrowUp  move the highlight (it starts on the current
 *                        value, and jumps to the top match as you type)
 *   Home / End           first / last option. This takes Home / End
 *                        away from the text caret — a trade the ARIA
 *                        pattern allows; the query is a few characters.
 *   Enter                pick the highlighted option
 *   Escape               close, focus back on the trigger
 *   Tab                  close and continue from the trigger, not from
 *                        the end of <body> where the portal lives
 * ──────────────────────────────────────────────────────────── */

const SearchableSelect = ({
  label,
  placeholder,
  value,
  onChange,
  options,
  searchValue,
  onSearchChange,
  loading,
  emptyMessage,
  loadingMessage,
  disabled,
  clearable,
  error,
  helperText,
  id,
  className,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState("");
  // Index into `filteredOptions` of the highlighted row, -1 for none.
  // Separate from `value`: arrowing only moves the highlight, Enter
  // commits it.
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchBarRef = useRef(null);
  const listRef = useRef(null);
  // True while the highlight was last moved by the keyboard (or by
  // opening). Only then does the list scroll to follow it — scrolling
  // under a hovering pointer would slide a new row beneath it and make
  // the highlight creep.
  const scrollActiveRef = useRef(false);

  // useId rather than the `id` prop, which belongs to the trigger and
  // is not guaranteed unique across the page.
  const listboxId = `${useId()}-listbox`;
  const optionId = (index) => `${listboxId}-option-${index}`;

  // Popup is rendered into a portal at <body> so it escapes any ancestor
  // `overflow: hidden` (e.g. PagePanel's clip rule) and any stacking
  // context that would otherwise put it behind sticky table headers. The
  // position is computed from the trigger's bounding rect and kept in
  // sync on scroll/resize while the popup is open.
  const [popupRect, setPopupRect] = useState(null);

  // The popup is a new node on every open; keeping it in state (callback
  // ref) lets a layout effect re-measure as soon as it exists.
  const [popupEl, setPopupEl] = useState(null);
  const setPopupNode = useCallback((node) => {
    popupRef.current = node;
    setPopupEl(node);
  }, []);

  // Flip / clamp decision — see computePopupPlacement. Unlike Select,
  // the decision uses the popup's FULL height (search bar + a list at
  // its 240px cap), not what is on screen right now: the list here
  // changes as the user types or async results land, so deciding on
  // the current height would open a cramped popup over a "Loading…"
  // row, or jump sides between keystrokes while the user is typing.
  const updatePopupRect = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    // Search bar (measured once mounted, ~45px before) + popup borders.
    const chrome = (searchBarRef.current?.offsetHeight || 45) + 2;
    const next = computePopupPlacement({
      trigger: trigger.getBoundingClientRect(),
      viewportHeight: getViewportHeight(),
      popupHeight: chrome + LIST_MAX_HEIGHT,
    });
    // Returning `prev` skips the re-render — this runs on every scroll.
    setPopupRect((prev) => (samePlacement(prev, next) ? prev : next));
  };

  const isControlledSearch = typeof searchValue === "string";
  const search = isControlledSearch ? searchValue : internalSearch;

  const setSearch = (q) => {
    if (!isControlledSearch) setInternalSearch(q);
    onSearchChange?.(q);
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      // Trigger lives in the wrapper; the popup is portaled to <body>.
      // Either container hosting the click is "inside" — anything else
      // closes the menu.
      const inTrigger =
        wrapperRef.current && wrapperRef.current.contains(e.target);
      const inPopup = popupRef.current && popupRef.current.contains(e.target);
      if (!inTrigger && !inPopup) setOpen(false);
    };
    // Fallback for Escape pressed while focus is outside the search box
    // (Escape inside it is handled — and stopped — in onSearchKeyDown).
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Lock the popup to the trigger's current rect while it's open. Run
  // synchronously after layout (useLayoutEffect) on open so the very
  // first paint has the popup correctly placed, then attach passive
  // scroll/resize listeners on the window AND on every scrollable
  // ancestor (a PagePanel inside a sticky-table region scrolls
  // internally, and the popup needs to follow that, not just window
  // scroll). Each of those re-takes the flip / clamp decision too.
  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePopupRect();
    const scrollParents = [];
    let node = triggerRef.current?.parentElement;
    while (node && node !== document.body) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        scrollParents.push(node);
      }
      node = node.parentElement;
    }
    const handler = () => updatePopupRect();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    scrollParents.forEach((n) => n.addEventListener("scroll", handler));
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
      scrollParents.forEach((n) => n.removeEventListener("scroll", handler));
    };
    // updatePopupRect only reads refs, so the copy captured on open
    // stays correct for as long as the popup is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Second pass once the popup has mounted and the search bar's real
  // height can be read. Still before paint.
  useLayoutEffect(() => {
    if (open && popupEl) updatePopupRect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, popupEl]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Reset internal search when the popup closes so reopening the menu
  // doesn't surprise the user with a stale filter.
  useEffect(() => {
    if (!open && !isControlledSearch) setInternalSearch("");
  }, [open, isControlledSearch]);

  const selectedOption = useMemo(() => {
    if (value == null || value === "") return null;
    return (
      options.find((o) => String(o.value) === String(value)) || {
        value,
        label: String(value),
      }
    );
  }, [value, options]);

  const filteredOptions = useMemo(() => {
    if (isControlledSearch) return options;
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const haystack = `${o.label || ""} ${o.sublabel || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, search, isControlledSearch]);

  // Results can change under the highlight (typing, async results);
  // never point aria-activedescendant at a row that is gone.
  const activeIdx =
    !loading && activeIndex >= 0 && activeIndex < filteredOptions.length
      ? activeIndex
      : -1;

  // Keep the keyboard highlight visible in a list longer than the popup.
  // popupRect is a dependency because the list's visible height is only
  // final after the flip / clamp pass; scrolling is a no-op when the row
  // is already in view.
  useLayoutEffect(() => {
    if (!open || !popupEl || activeIdx < 0 || !scrollActiveRef.current) return;
    scrollOptionIntoView(listRef.current, document.getElementById(optionId(activeIdx)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, popupEl, activeIdx, popupRect]);

  const isSelected = (o) =>
    !!selectedOption && String(selectedOption.value) === String(o.value);

  const openMenu = () => {
    scrollActiveRef.current = true;
    setActiveIndex(initialActiveIndex(filteredOptions, isSelected));
    setOpen(true);
  };

  const closeMenu = ({ restoreFocus = false } = {}) => {
    // Focus goes back BEFORE the popup unmounts: the search box holding
    // focus is about to be removed, which would drop focus onto <body>
    // and restart Tab order from the top of the page. preventScroll —
    // the trigger is on screen already; focus() must not nudge the page.
    if (restoreFocus) triggerRef.current?.focus({ preventScroll: true });
    setOpen(false);
  };

  const select = (val,o) => {
    onChange?.(val,o);
    closeMenu({ restoreFocus: true });
  };

  const onSearchKeyDown = (e) => {
    const next = nextActiveIndex(e.key, loading ? [] : filteredOptions, activeIdx);
    if (next !== null) {
      // preventDefault keeps the caret / page still; stopPropagation keeps
      // an ancestor's own key handling from reacting to the same press
      // (the popup is a portal, but React still bubbles through it).
      e.preventDefault();
      e.stopPropagation();
      scrollActiveRef.current = true;
      setActiveIndex(next);
      return;
    }
    switch (e.key) {
      case "Enter": {
        // Always swallowed while open, so Enter in the search box never
        // reaches a surrounding form's submit handling.
        e.preventDefault();
        e.stopPropagation();
        const option = filteredOptions[activeIdx];
        if (isOptionEnabled(option)) select(option.value, option);
        break;
      }
      case "Escape":
        // Stopped so Escape closes only this list, not also the modal
        // or drawer this field sits in.
        e.preventDefault();
        e.stopPropagation();
        closeMenu({ restoreFocus: true });
        break;
      case "Tab":
        // Moving focus to the trigger first (no preventDefault) makes the
        // browser's own Tab / Shift+Tab continue from the field itself.
        closeMenu({ restoreFocus: true });
        break;
      default:
        break;
    }
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const triggerClasses = [
    styles.trigger,
    open ? styles.triggerOpen : "",
    error ? styles.triggerError : "",
    disabled ? styles.triggerDisabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  const wrapperClasses = [styles.wrapper, className || ""]
    .filter(Boolean)
    .join(" ");

  const showClear = clearable && !disabled && selectedOption;

  return (
    <div className={wrapperClasses} ref={wrapperRef}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}
      <div
        id={id}
        ref={triggerRef}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        // Same reasoning as Select.js: this trigger is a <div>, so the
        // HTML `disabled` attribute does nothing for it. Without
        // aria-disabled a greyed-out, tabIndex -1 field is simply
        // missing from the keyboard tour with no explanation — the
        // screen reader never mentions it at all.
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        className={triggerClasses}
        onClick={() => {
          if (disabled) return;
          if (open) closeMenu();
          else openMenu();
        }}
        onKeyDown={(e) => {
          // While open, keys belong to the search box (focus moves there
          // on open); this handler only opens the list.
          if (disabled || open) return;
          if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
            e.preventDefault();
            openMenu();
          }
        }}
      >
        <span className={styles.value}>
          {selectedOption ? (
            <span className={styles.valueLabel}>{selectedOption.label}</span>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </span>

        {showClear && (
          <button
            type="button"
            className={styles.clear}
            aria-label="Clear selection"
            onClick={clear}
          >
            <X size={14} />
          </button>
        )}

        <span
          className={[styles.caret, open ? styles.caretOpen : ""]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
        >
          <ChevronDown size={16} />
        </span>
      </div>

      {open && popupRect &&
        createPortal(
          <div
            ref={setPopupNode}
            className={styles.popup}
            data-placement={popupRect.placement}
            style={{
              position: "fixed",
              // Exactly one of top / bottom is set: a popup flipped above
              // the trigger is anchored by its bottom edge, so it stays
              // flush with the trigger as the result list shrinks/grows.
              ...(popupRect.top != null ? { top: popupRect.top } : {}),
              ...(popupRect.bottom != null ? { bottom: popupRect.bottom } : {}),
              left: popupRect.left,
              width: popupRect.width,
              maxHeight: popupRect.maxHeight,
            }}
          >
            <div className={styles.searchBar} ref={searchBarRef}>
            <span className={styles.searchIcon}>
              <Search size={14} />
            </span>
            <input
              ref={searchInputRef}
              type="text"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded="true"
              aria-controls={listboxId}
              aria-activedescendant={activeIdx >= 0 ? optionId(activeIdx) : undefined}
              aria-label={`Search ${label || ariaLabel || "options"}`}
              autoComplete="off"
              className={styles.searchInput}
              placeholder="Search…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                // New query, new list: highlight its top match so Enter
                // picks what the user is looking at.
                scrollActiveRef.current = true;
                setActiveIndex(0);
              }}
              onKeyDown={onSearchKeyDown}
            />
          </div>
          {loading ? (
            <div className={styles.loadingRow}>{loadingMessage}</div>
          ) : filteredOptions.length === 0 ? (
            <div className={styles.empty}>{emptyMessage}</div>
          ) : (
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label={label || ariaLabel || undefined}
              className={styles.list}
            >
              {filteredOptions.map((o, i) => {
                const checked = isSelected(o);
                const isActive = i === activeIdx;
                const cls = [
                  styles.option,
                  checked ? styles.optionSelected : "",
                  isActive ? styles.optionActive : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                // No key handler on rows: focus stays in the search box
                // and drives them through aria-activedescendant.
                return (
                  <li
                    key={o.value}
                    id={optionId(i)}
                    role="option"
                    aria-selected={checked}
                    className={cls}
                    // Keep focus in the search box when a row is pressed,
                    // so typing / arrowing still works if the click is
                    // dragged off the row and nothing gets picked.
                    onMouseDown={(e) => e.preventDefault()}
                    // mousemove, not mouseenter: keyboard scrolling slides
                    // rows under a still pointer and must not steal the
                    // highlight.
                    onMouseMove={() => {
                      if (i === activeIdx) return;
                      scrollActiveRef.current = false;
                      setActiveIndex(i);
                    }}
                    onClick={() => select(o.value,o)}
                  >
                    <span className={styles.optionBody}>
                      <div className={styles.optionLabel}>{o.label}</div>
                      {o.sublabel && (
                        <div className={styles.optionSub}>{o.sublabel}</div>
                      )}
                    </span>
                    {checked && (
                      <span className={styles.optionCheck} aria-hidden="true">
                        <Check size={14} strokeWidth={3} />
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          </div>,
          document.body
        )}

      {error && <span className={styles.error}>{error}</span>}
      {!error && helperText && (
        <span className={styles.helperText}>{helperText}</span>
      )}
    </div>
  );
};

SearchableSelect.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
      sublabel: PropTypes.string,
    })
  ),
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  loadingMessage: PropTypes.string,
  disabled: PropTypes.bool,
  clearable: PropTypes.bool,
  error: PropTypes.string,
  helperText: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

SearchableSelect.defaultProps = {
  label: "",
  placeholder: "Select…",
  value: null,
  options: [],
  searchValue: undefined,
  onSearchChange: undefined,
  loading: false,
  emptyMessage: "No options found",
  loadingMessage: "Loading…",
  disabled: false,
  clearable: false,
  error: "",
  helperText: "",
  id: undefined,
  className: "",
  ariaLabel: undefined,
};

export default SearchableSelect;
